import React, { forwardRef } from "react";
import "./Card.scss";

// Fontaine M35 — Card (`ft-card`). The ONE generic panel surface: a bordered, radiused box on a
// raised surface. Visual contract: Style Guide V2 `card-solid` / `card-raised` specimens.
//
// It deliberately knows nothing about its contents. Anything with a fixed internal design
// (the listing card, the Kanban card) is its own composite, not a Card variant — Card is the
// plain surface those composites would otherwise each re-declare.

const SURFACES = ["default", "raised"];
// A scale, not a free-for-all: `none` is the edge-to-edge case (media, tables, list rows that
// own their own padding), the other three are the V2 spacing steps.
const PADDINGS = ["none", "sm", "md", "lg"];
// Does the browser already give this element activation semantics of its own? A `button` always
// does. An `a` only does when it has an `href` — a bare `<a onClick>` is neither focusable nor
// Enter-activatable, so it needs exactly the same synthesis a `<div>` does.
const isNativelyInteractive = (Component, props) =>
  typeof Component === "string" &&
  (Component === "button" || (Component === "a" && props.href != null));

/**
 * @param {string|React.ElementType} [as="div"]        semantic element — section, article, li, button, a…
 * @param {"default"|"raised"} [surface="default"]     default = page-level panel, raised = on top of one
 * @param {boolean} [interactive=false]                hover lift + focus ring; wires keyboard activation
 *                                                     when `onClick` is present and the element is not
 *                                                     natively interactive (a `button`, or an `a`
 *                                                     that actually has an `href`)
 * @param {"none"|"sm"|"md"|"lg"} [padding="md"]        `none` for edge-to-edge content
 * @param {Function} [onClick]                         with `interactive`, becomes keyboard-activatable
 * @param {string} [className] / {ReactNode} [children] / ref                 passthrough
 */
const Card = forwardRef(function Card(
  {
    as: Component = "div",
    surface = "default",
    interactive = false,
    padding = "md",
    onClick,
    className = "",
    children,
    ...rest
  },
  ref
) {
  const resolvedSurface = SURFACES.includes(surface) ? surface : "default";
  const resolvedPadding = PADDINGS.includes(padding) ? padding : "md";

  // Keyboard access is only synthesised for the case that actually needs it: a clickable card
  // on an element the browser gives no activation behaviour to. A `button` or a real `href`
  // anchor already has Enter (and a focus ring), so we leave those completely alone.
  const isNative = isNativelyInteractive(Component, rest);
  const needsKeyboard = interactive && typeof onClick === "function" && !isNative;
  const activate = needsKeyboard
    ? (event) => {
        if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
        // Space scrolls the page by default; Enter on a role=button must not submit a form.
        event.preventDefault();
        onClick(event);
      }
    : undefined;

  const classes = [
    "ft-card",
    `ft-card--${resolvedSurface}`,
    `ft-card--pad-${resolvedPadding}`,
    interactive ? "ft-card--interactive" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Component
      ref={ref}
      className={classes}
      onClick={onClick}
      // Only the synthesised case gets role / tabIndex — both are wrong on a real button, and a
      // caller-supplied role or tabIndex always wins via `rest`.
      role={needsKeyboard ? "button" : undefined}
      tabIndex={needsKeyboard ? 0 : undefined}
      onKeyDown={activate}
      type={Component === "button" ? rest.type || "button" : rest.type}
      {...rest}
    >
      {children}
    </Component>
  );
});

export default Card;
